import React from 'react'
import { useIntl } from 'react-intl'
import { Link, useLocation } from 'react-router-dom'
import {
    Box,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material'
import ArrowRightIcon from '../../../../assets/icons/Arrows-chevron/Arrow-Right.svg'
import { StudentSectionAnalysisState } from '../../../../features/types'
import ProgressBar from '../../../../components/progressBar/ProgressBar'
import { tableStyles, ui } from '../../../../utils/uiTokens'
import { capitalize } from '../../../../utils/functions'
import styled from '@emotion/styled'

interface Props {
    students: StudentSectionAnalysisState[]
    isFetching: boolean
    sortColumn?: number
    sortDir?: number
    onSort?: (column: number) => void
}

// Заголовок-кнопка со стрелками ▲▼: активное направление подсвечено
const SortableHeader: React.FC<{
    label: string
    column: number
    sortColumn?: number
    sortDir?: number
    onSort?: (column: number) => void
}> = ({ label, column, sortColumn, sortDir, onSort }) => {
    const active = sortColumn === column
    return (
        <HeaderButton type="button" onClick={() => onSort?.(column)}>
            {label}
            <Arrows>
                <span style={{ color: active && sortDir === 2 ? ui.pink : ui.muted }}>▲</span>
                <span style={{ color: active && sortDir === 1 ? ui.pink : ui.muted }}>▼</span>
            </Arrows>
        </HeaderButton>
    )
}

const GroupStudentsTable: React.FC<Props> = ({
    students = [],
    isFetching = false,
    sortColumn,
    sortDir,
    onSort,
}) => {
    // ссылки абсолютные: карточка листает соседей и сама живёт по другому пути
    const { pathname } = useLocation()
    const linkOf = (row: StudentSectionAnalysisState) =>
        `${pathname.replace(/\/$/, '')}/${row?.userId}/${row?.userCourseEnrollId}?courseSubjectId=${row?.courseSubjectId}`
    const siblings = students.map((row) => ({ id: row.userCourseEnrollId, to: linkOf(row), student: row }))
    const { formatMessage } = useIntl()

    return (
        <Box>
            <TableContainer
                component={Paper}
                sx={tableStyles.container}
                elevation={0}
            >
                <Table
                    sx={{ minWidth: 500 }}
                    aria-label="custom pagination table"
                >
                    <TableHead sx={tableStyles.head}>
                        <TableRow>
                            <TableCell>#</TableCell>

                            <TableCell>
                                {formatMessage({ id: 'fullName' })}
                            </TableCell>

                            <TableCell>
                                {formatMessage({ id: 'group' })}
                            </TableCell>

                            <TableCell>
                                <SortableHeader
                                    label={capitalize(formatMessage({ id: 'videoLesson' }))}
                                    column={1}
                                    sortColumn={sortColumn}
                                    sortDir={sortDir}
                                    onSort={onSort}
                                />
                            </TableCell>

                            <TableCell>
                                <SortableHeader
                                    label={formatMessage({ id: 'test' })}
                                    column={2}
                                    sortColumn={sortColumn}
                                    sortDir={sortDir}
                                    onSort={onSort}
                                />
                            </TableCell>

                            <TableCell align="right">
                                {formatMessage({ id: 'details' })}
                            </TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {students.length
                            ? students.map((row, index) => (
                                  <TableRow key={row.userId} sx={tableStyles.row}>
                                      <TableCell
                                          component="th"
                                          scope="row"
                                      >
                                          {index + 1}
                                      </TableCell>

                                      <TableCell
                                          component="th"
                                          scope="row"
                                      >
                                          {row.firstName + ' ' + row.lastName}
                                      </TableCell>

                                      <TableCell
                                          component="th"
                                          scope="row"
                                      >
                                          {row.title}
                                      </TableCell>

                                      <TableCell
                                          component="th"
                                          scope="row"
                                      >
                                          <ProgressBar value={row.watchedVideoPercentage} />
                                      </TableCell>

                                      <TableCell
                                          component="th"
                                          scope="row"
                                      >
                                          <ProgressBar value={row.topicTestPercentage} />
                                      </TableCell>

                                      <TableCell
                                          style={{ width: 60 }}
                                          align="right"
                                      >
                                          <Link
                                              to={linkOf(row)}
                                              state={{
                                                  student: row,
                                                  siblings,
                                              }}
                                          >
                                              <IconButton
                                                  sx={{ background: ui.pinkSoft, borderRadius: '10px' }}
                                              >
                                                  <img
                                                      src={ArrowRightIcon}
                                                      alt="edit"
                                                  />
                                              </IconButton>
                                          </Link>
                                      </TableCell>
                                  </TableRow>
                              ))
                            : !isFetching && (
                                  <TableRow>
                                      <TableCell
                                          component="th"
                                          scope="row"
                                          align="center"
                                          colSpan={10}
                                      >
                                          {formatMessage({ id: 'noData' })}
                                      </TableCell>
                                  </TableRow>
                              )}
                    </TableBody>

                </Table>
            </TableContainer>
        </Box>
    )
}

export default GroupStudentsTable

const HeaderButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    font: inherit;
    color: inherit;
    user-select: none;
`

const Arrows = styled.span`
    display: inline-flex;
    flex-direction: column;
    line-height: 0.75;
    font-size: 9px;
`
